import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Input as InputIcon,
  Output as OutputIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

// ──────────────────────────────────────────────────────────────────────────
// PluginUsageInfo
// ──────────────────────────────────────────────────────────────────────────
// Renders a uniform "what does this plugin take in / what does it return"
// panel for any plugin object that has a `usage_info` block (emitted by the
// backend `PluginFunctionMetadata.to_dict()` in plugin_system.py).
//
// Two visual modes:
//   compact: collapsed summary chips + expandable details (for the side panel)
//   full:    everything visible at once (for the Full Config dialog)
//
// Falls back gracefully when a plugin has no usage_info (older API responses
// or non-plugin functions): renders nothing.
// ──────────────────────────────────────────────────────────────────────────

export interface PluginUsageField {
  name: string;
  type: string;
  required: boolean;
  description?: string | null;
}

export interface PluginUsageOutput {
  type: string;
  object_name?: string | null;
  fields?: string[];
  example?: any;
}

export interface PluginUsageInfoData {
  inputs: PluginUsageField[];
  output: PluginUsageOutput;
  example?: { input?: any; output?: any } | null;
  summary?: string;
}

/** Plugins that submit an LLM batch job. The chain node's AI Model dropdown
 *  is the model the batch actually runs (GPT → OpenAI Batch, Gemini → Vertex).
 *  Other plugins ignore that dropdown. */
export const LLM_BATCH_PLUGIN_NAMES = new Set([
  'submit_llm_batch',
  'submit_autotag_batch',
]);

export function isLlmBatchPlugin(functionIdOrName?: string | null): boolean {
  if (!functionIdOrName) return false;
  const name = String(functionIdOrName).replace(/^plugin:/, '');
  return LLM_BATCH_PLUGIN_NAMES.has(name);
}

export interface PluginLikeObject {
  name?: string;
  display_name?: string;
  description?: string;
  output_type?: string;
  required_params?: string[];
  optional_params?: string[];
  examples?: Array<{ input?: any; output?: any }>;
  usage_info?: PluginUsageInfoData;
  response_format?: { object_name?: string };
}

interface PluginUsageInfoProps {
  plugin: PluginLikeObject | null | undefined;
  variant?: 'compact' | 'full';
  showExample?: boolean;
  maxExampleHeight?: number;
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  string: '#1565c0',
  integer: '#6a1b9a',
  number: '#6a1b9a',
  boolean: '#2e7d32',
  object: '#ef6c00',
  array: '#c62828',
  any: '#616161',
};

const typeColor = (t: string): string => {
  const base = (t || '').replace(/<.*>$/, '').toLowerCase();
  return TYPE_COLORS[base] || '#616161';
};

const CopyButton: React.FC<{ text: string; size?: 'small' | 'medium' }> = ({ text, size = 'small' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy JSON'}>
      <IconButton
        size={size}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        sx={{ p: 0.25 }}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
        ) : (
          <CopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Field row
// ──────────────────────────────────────────────────────────────────────────

const FieldRow: React.FC<{ field: PluginUsageField }> = ({ field }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      py: 0.5,
      px: 1,
      borderRadius: 0.5,
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Box
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'text.primary',
        minWidth: 0,
        wordBreak: 'break-all',
      }}
    >
      {field.name}
    </Box>
    <Chip
      label={field.type}
      size="small"
      sx={{
        height: 16,
        fontSize: '0.6rem',
        fontFamily: 'monospace',
        bgcolor: 'transparent',
        color: typeColor(field.type),
        border: `1px solid ${typeColor(field.type)}33`,
        flexShrink: 0,
      }}
    />
    {field.required && (
      <Chip
        label="required"
        size="small"
        sx={{
          height: 16,
          fontSize: '0.55rem',
          fontWeight: 700,
          bgcolor: '#ffebee',
          color: '#c62828',
          flexShrink: 0,
        }}
      />
    )}
    {field.description && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.7rem', lineHeight: 1.3, ml: 'auto', maxWidth: '60%', textAlign: 'right' }}
      >
        {field.description}
      </Typography>
    )}
  </Box>
);

// ──────────────────────────────────────────────────────────────────────────
// Section header
// ──────────────────────────────────────────────────────────────────────────

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  label: string;
  count?: number;
  color?: string;
}> = ({ icon, label, count, color = '#1976d2' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
    <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Typography
      variant="overline"
      sx={{
        fontSize: '0.65rem',
        fontWeight: 700,
        color,
        lineHeight: 1.2,
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    {typeof count === 'number' && (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
        ({count})
      </Typography>
    )}
  </Box>
);

// ──────────────────────────────────────────────────────────────────────────
// Example block
// ──────────────────────────────────────────────────────────────────────────

const ExampleBlock: React.FC<{
  example: { input?: any; output?: any };
  maxHeight: number;
  startCollapsed?: boolean;
}> = ({ example, maxHeight, startCollapsed = true }) => {
  const [open, setOpen] = useState(!startCollapsed);
  const inputJson = example.input != null ? JSON.stringify(example.input, null, 2) : '';
  const outputJson = example.output != null ? JSON.stringify(example.output, null, 2) : '';

  return (
    <Box>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          color: 'text.secondary',
          '&:hover': { color: 'text.primary' },
        }}
      >
        {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
          Example {open ? '(click to collapse)' : '(click to expand)'}
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: 0.75 }}>
          {inputJson && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 700 }}
                >
                  example input
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <CopyButton text={inputJson} />
                </Box>
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  lineHeight: 1.4,
                  maxHeight,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {inputJson}
              </Box>
            </Box>
          )}
          {outputJson && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 700 }}
                >
                  example output
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <CopyButton text={outputJson} />
                </Box>
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  lineHeight: 1.4,
                  maxHeight,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {outputJson}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Fallback usage_info (when backend hasn't been deployed yet)
// ──────────────────────────────────────────────────────────────────────────

const synthesizeUsageInfo = (plugin: PluginLikeObject): PluginUsageInfoData | null => {
  const required = plugin.required_params || [];
  const optional = plugin.optional_params || [];
  if (required.length === 0 && optional.length === 0 && !plugin.examples?.length) return null;
  const inputs: PluginUsageField[] = [
    ...required.map((n) => ({ name: n, type: 'any', required: true })),
    ...optional.map((n) => ({ name: n, type: 'any', required: false })),
  ];
  const ex = plugin.examples?.[0];
  return {
    inputs,
    output: {
      type: plugin.output_type || 'json',
      object_name: plugin.response_format?.object_name || null,
      example: ex?.output,
    },
    example: ex || null,
    summary: undefined,
  };
};

// ──────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────

export const PluginUsageInfo: React.FC<PluginUsageInfoProps> = ({
  plugin,
  variant = 'full',
  showExample = true,
  maxExampleHeight = 220,
}) => {
  if (!plugin) return null;

  const usage: PluginUsageInfoData | null = plugin.usage_info || synthesizeUsageInfo(plugin);
  if (!usage) {
    // No structured info — show description if available, otherwise nothing
    if (!plugin.description) return null;
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
        {plugin.description}
      </Typography>
    );
  }

  const requiredFields = (usage.inputs || []).filter((f) => f.required);
  const optionalFields = (usage.inputs || []).filter((f) => !f.required);
  const hasInputs = requiredFields.length + optionalFields.length > 0;
  const hasExample =
    showExample && usage.example && (usage.example.input != null || usage.example.output != null);
  const outputLabel = usage.output.object_name
    ? `${usage.output.type} (${usage.output.object_name})`
    : usage.output.type;

  // ─────────── COMPACT VARIANT ───────────
  // Used in the right-side panel. Inputs/output always visible, example collapsed by default.
  if (variant === 'compact') {
    return (
      <Box>
        {usage.summary && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1, fontSize: '0.7rem', lineHeight: 1.4 }}
          >
            {usage.summary}
          </Typography>
        )}

        {hasInputs && (
          <Box sx={{ mb: 1.5 }}>
            <SectionHeader
              icon={<InputIcon sx={{ fontSize: 14 }} />}
              label="Inputs"
              count={requiredFields.length + optionalFields.length}
              color="#1565c0"
            />
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.5,
                py: 0.25,
              }}
            >
              {requiredFields.map((f) => (
                <FieldRow key={`req-${f.name}`} field={f} />
              ))}
              {optionalFields.length > 0 && requiredFields.length > 0 && (
                <Divider sx={{ my: 0.25 }} />
              )}
              {optionalFields.map((f) => (
                <FieldRow key={`opt-${f.name}`} field={f} />
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 1.5 }}>
          <SectionHeader
            icon={<OutputIcon sx={{ fontSize: 14 }} />}
            label="Returns"
            color="#7b1fa2"
          />
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              p: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip
                label={outputLabel}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontFamily: 'monospace',
                  bgcolor: '#f3e5f5',
                  color: '#7b1fa2',
                  fontWeight: 600,
                }}
              />
            </Box>
            {usage.output.fields && usage.output.fields.length > 0 && (
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {usage.output.fields.slice(0, 12).map((f) => (
                  <Box
                    key={f}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      px: 0.5,
                      borderRadius: 0.25,
                      bgcolor: 'grey.100',
                    }}
                  >
                    {f}
                  </Box>
                ))}
                {usage.output.fields.length > 12 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    +{usage.output.fields.length - 12} more
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {hasExample && (
          <ExampleBlock
            example={usage.example as { input?: any; output?: any }}
            maxHeight={maxExampleHeight}
            startCollapsed
          />
        )}
      </Box>
    );
  }

  // ─────────── FULL VARIANT ───────────
  // Used in the Full Config dialog. Everything visible, example pre-expanded.
  return (
    <Box>
      {usage.summary && (
        <Box
          sx={{
            mb: 1.5,
            p: 1,
            bgcolor: 'info.50',
            border: '1px solid',
            borderColor: 'info.light',
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CodeIcon sx={{ fontSize: 16, color: 'info.main' }} />
          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
            {usage.summary}
          </Typography>
        </Box>
      )}

      {hasInputs && (
        <Box sx={{ mb: 2 }}>
          <SectionHeader
            icon={<InputIcon sx={{ fontSize: 16 }} />}
            label="Inputs"
            count={requiredFields.length + optionalFields.length}
            color="#1565c0"
          />
          {requiredFields.length > 0 && (
            <Box sx={{ mb: optionalFields.length > 0 ? 1 : 0 }}>
              <Typography
                variant="caption"
                color="error.main"
                sx={{ fontSize: '0.65rem', fontWeight: 700, display: 'block', mb: 0.25, ml: 1 }}
              >
                REQUIRED
              </Typography>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  py: 0.25,
                }}
              >
                {requiredFields.map((f) => (
                  <FieldRow key={`req-${f.name}`} field={f} />
                ))}
              </Box>
            </Box>
          )}
          {optionalFields.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem', fontWeight: 700, display: 'block', mb: 0.25, ml: 1 }}
              >
                OPTIONAL
              </Typography>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  py: 0.25,
                }}
              >
                {optionalFields.map((f) => (
                  <FieldRow key={`opt-${f.name}`} field={f} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <SectionHeader
          icon={<OutputIcon sx={{ fontSize: 16 }} />}
          label="Returns"
          color="#7b1fa2"
        />
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 0.5,
            p: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
            <Chip
              label={outputLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontFamily: 'monospace',
                bgcolor: '#f3e5f5',
                color: '#7b1fa2',
                fontWeight: 600,
              }}
            />
          </Box>
          {usage.output.fields && usage.output.fields.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem', fontWeight: 700 }}
              >
                fields:
              </Typography>
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {usage.output.fields.map((f) => (
                  <Box
                    key={f}
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      color: 'text.primary',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {f}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {hasExample && (
        <Box>
          <SectionHeader
            icon={<CodeIcon sx={{ fontSize: 16 }} />}
            label="Example"
            color="#2e7d32"
          />
          <ExampleBlock
            example={usage.example as { input?: any; output?: any }}
            maxHeight={maxExampleHeight}
            startCollapsed={false}
          />
        </Box>
      )}
    </Box>
  );
};

export default PluginUsageInfo;
